import { formatApiErrorDetail } from "@/lib/api";
import { displayLocalDate } from "@/lib/dateHelpers";
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import FieldRadarLogo from "@/components/walk/FieldRadarLogo";
import { AdminShell } from "@/components/Layout";
import {
  ClipboardText, Plus, MapPin, CalendarBlank, User, FilePdf, MagicWand,
  LinkSimple, Copy, Sparkle,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminPropertyInspections — RentCheck-parity admin-scheduled inspections.
 * Sibling to TenantRadar (tenant-self-guided).
 * Route: /admin/property-inspections
 */
export default function AdminPropertyInspections() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const [inspections, setInspections] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/api/inspections`, auth());
      setInspections(r.data.inspections || []);
    } catch {
      toast.error("Couldn't load inspections");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="property-inspections-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ClipboardText weight="fill" size={30} className="text-violet-600"/>
            <div>
              <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
                Property Inspections
              </h1>
              <div className="text-sm text-slate-600 font-medium mt-0.5">
                Move-in, move-out, periodic — every room, every photo, one PDF. Replaces RentCheck.
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowNew(true)}
            data-testid="new-inspection-btn"
            className="h-10 px-4 bg-violet-600 hover:bg-violet-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-1.5"
          >
            <Plus weight="bold" size={14}/> New Inspection
          </button>
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-slate-400">Loading…</div>
        ) : inspections.length === 0 ? (
          <div className="mt-8 p-12 text-center border-2 border-dashed border-slate-200">
            <ClipboardText size={44} className="mx-auto text-slate-300"/>
            <div className="mt-3 font-black uppercase tracking-widest text-slate-600">
              No inspections yet
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Start with a move-out or periodic inspection.
            </div>
            <button onClick={() => setShowNew(true)}
              className="mt-4 h-9 px-3 bg-violet-600 hover:bg-violet-700 text-white font-black uppercase text-[10px] tracking-widest">
              Start Your First
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            {inspections.map(i => <Card key={i.id} i={i}/>)}
          </div>
        )}
      </div>

      {showNew && <NewModal onClose={() => setShowNew(false)} onCreated={(i) => {
        setInspections(prev => [i, ...prev]); setShowNew(false); toast.success("Inspection created");
      }}/>}
    </AdminShell>
  );
}

function Card({ i }) {
  const pct = i.progress.pct;
  const statusColor = {
    draft: "bg-slate-200 text-slate-700",
    started: "bg-cyan-100 text-cyan-800",
    submitted: "bg-emerald-100 text-emerald-800",
  }[i.status] || "bg-slate-200 text-slate-700";

  const downloadPdf = async (e, opts = {}) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const token = localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
      const q = new URLSearchParams();
      if (opts.include_narrative) q.set("include_narrative", "1");
      if (opts.generate_narrative) q.set("generate_narrative", "1");
      q.set("auth", token);
      const url = `${BASE}/api/inspections/${i.id}/pdf?${q}`;
      // Open directly — the browser handles the download natively (works on Android/iOS where blob downloads get blocked)
      const w = window.open(url, "_blank");
      if (!w) window.location.href = url;
      toast.success(opts.generate_narrative ? "Building PDF with AI summary — opens in a new tab (20–40s)" : "PDF opening in a new tab");
    } catch (err) {
      toast.error("PDF export failed");
    }
  };

  const mintTenantLink = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const token = localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
      const r = await axios.post(`${BASE}/api/inspections/${i.id}/tenant-link`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      try {
        await navigator.clipboard.writeText(r.data.url);
        toast.success("Tenant link copied — valid 14 days");
      } catch {
        // Fallback for older browsers / non-secure contexts
        window.prompt("Copy this tenant link (valid 14 days):", r.data.url);
      }
    } catch (err) {
      toast.error("Couldn't mint tenant link");
    }
  };

  return (
    <Link to={`/admin/property-inspections/${i.id}`}
      data-testid={`inspection-${i.id}`}
      className="block border-2 border-slate-200 bg-white hover:border-violet-500 transition">
      <div className="p-4 flex items-start gap-3 flex-wrap sm:flex-nowrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${statusColor}`}>{i.status}</span>
            <span className="text-[10px] font-black uppercase tracking-widest text-violet-700">{i.template_name}</span>
          </div>
          <div className="mt-1 font-black text-lg text-slate-900 flex items-center gap-1.5">
            <MapPin weight="fill" size={14} className="text-cyan-600 flex-none"/>
            <span className="truncate">{i.property_address}</span>
          </div>
          <div className="mt-1 text-xs text-slate-600 flex items-center gap-3 flex-wrap">
            {i.due_date && <span className="inline-flex items-center gap-1"><CalendarBlank size={11}/> Due {displayLocalDate(i.due_date)}</span>}
            {i.assigned_to_name && <span className="inline-flex items-center gap-1"><User size={11}/> {i.assigned_to_name}</span>}
            <span className="text-slate-500">{i.progress.done_steps} / {i.progress.total_steps} steps</span>
          </div>
          <div className="mt-2 h-1.5 bg-slate-100 overflow-hidden">
            <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-violet-500"}`} style={{ width: `${pct}%` }}/>
          </div>
        </div>
        <div className="text-right w-full sm:w-auto sm:flex-none min-w-0 flex flex-row sm:flex-col items-center sm:items-end justify-between gap-2">
          <div>
            <div className="text-2xl font-black tabular-nums text-violet-700">{pct}%</div>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">complete</div>
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-end">
            <button onClick={mintTenantLink}
              data-testid={`tenant-link-${i.id}`}
              title="Mint magic link for tenant self-inspection (14 days)"
              className="h-7 px-2 border-2 border-cyan-400 hover:bg-cyan-50 text-cyan-700 font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
              <LinkSimple weight="bold" size={11}/> Tenant Link
            </button>
            <Link to={`/admin/property-inspections/${i.id}/compare`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`compare-${i.id}`}
              title="Compare with the paired Move-In / Move-Out inspection at the same address"
              className="h-7 px-2 border-2 border-fuchsia-400 hover:bg-fuchsia-50 text-fuchsia-700 font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
              <Sparkle weight="fill" size={11}/> Compare
            </Link>
            <button onClick={(e) => downloadPdf(e, { generate_narrative: true, include_narrative: true })}
              data-testid={`download-pdf-ai-${i.id}`}
              title="Generate AI executive summary and include on cover"
              className="h-7 px-2 border-2 border-fuchsia-400 hover:bg-fuchsia-50 text-fuchsia-700 font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
              <MagicWand weight="fill" size={11}/> PDF + AI
            </button>
            <button onClick={(e) => downloadPdf(e)}
              data-testid={`download-pdf-${i.id}`}
              title="Standard PDF (no AI summary)"
              className="h-7 px-2 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
              <FilePdf weight="fill" size={11} className="text-rose-600"/> PDF
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

function NewModal({ onClose, onCreated }) {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const [addresses, setAddresses] = useState([]);
  const [address, setAddress] = useState("");
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [kind, setKind] = useState("move_out");
  const [walker, setWalker] = useState("employee");
  const [allowLibrary, setAllowLibrary] = useState(true);
  const [beds, setBeds] = useState(2);
  const [fullBaths, setFullBaths] = useState(1);
  const [halfBaths, setHalfBaths] = useState(0);
  const OPTIONAL_ROOMS = [
    "Kitchen", "Living Room", "Dining Room", "Hallway", "Stairway",
    "Garage", "Basement", "Attic", "Laundry Room", "Foyer / Entry",
    "Yard", "Deck", "Porch", "Office", "Closet", "Pantry",
  ];
  const [rooms, setRooms] = useState({ "Kitchen": true, "Living Room": true, "Laundry Room": true });
  const [creating, setCreating] = useState(false);
  const [autofillSource, setAutofillSource] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/whatsapp/property-picker`, auth())
      .then(a => setAddresses(a.data.addresses || []))
      .catch(() => toast.error("Couldn't load properties"));
    // eslint-disable-next-line
  }, []);

  // Auto-fill bed/bath from AppFolio history when the admin picks a
  // known address. Debounced so typing feels natural.
  useEffect(() => {
    if (!address || address.trim().length < 3) { setAutofillSource(null); return; }
    const knownExact = addresses.some(a => a.address === address.trim());
    if (!knownExact) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${BASE}/api/inspections/lookup/property-config?address=${encodeURIComponent(address.trim())}`,
          auth());
        if (r.data.source) {
          if (r.data.bedrooms) setBeds(r.data.bedrooms);
          if (r.data.full_baths) setFullBaths(r.data.full_baths);
          if (r.data.half_baths) setHalfBaths(r.data.half_baths);
          setAutofillSource(r.data.source);
        } else {
          setAutofillSource(null);
        }
      } catch { /* silent */ }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [address, addresses]);

  const submit = async () => {
    if (!address.trim()) return toast.error("Type or pick an address");
    setCreating(true);
    try {
      const r = await axios.post(`${BASE}/api/inspections`, {
        kind, property_address: address.trim(), due_date: dueDate,
        walker, allow_library: walker === "tenant" ? false : allowLibrary,
        config: {
          bedrooms: beds, full_baths: fullBaths, half_baths: halfBaths,
          rooms: Object.entries(rooms).filter(([, v]) => v).map(([k]) => k),
        },
      }, auth());
      let created = r.data;
      if (walker === "tenant") {
        const l = await axios.post(`${BASE}/api/inspections/${r.data.id}/tenant-link`, {}, auth());
        created = { ...created, tenant_link_url: l.data.url };
        try { await navigator.clipboard.writeText(l.data.url); toast.success("Tenant link copied — text it to the tenant"); }
        catch { toast.success("Tenant link ready on the card"); }
      }
      onCreated(created);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Create failed"));
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/70 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="new-inspection-modal">
      <div onClick={e => e.stopPropagation()}
        className="bg-[#0b1c2d] text-white border border-white/10 rounded-3xl w-full max-w-lg p-5 space-y-4 my-8 shadow-2xl" data-testid="new-inspection-card">
        <div className="flex items-center justify-between">
          <FieldRadarLogo size={24} sub="New Inspection"/>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl">×</button>
        </div>

        {/* Type toggle */}
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-teal-400">Type</label>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {[
              { k: "move_in",  label: "MOVE-IN" },
              { k: "move_out", label: "MOVE-OUT" },
              { k: "periodic", label: "PERIODIC" },
            ].map(t => (
              <button key={t.k} onClick={() => setKind(t.k)}
                data-testid={`kind-${t.k}`}
                className={`h-11 rounded-xl border font-black uppercase text-[11px] tracking-widest ${
                  kind === t.k ? "bg-teal-500 border-teal-400 text-slate-900" : "bg-white/5 border-white/10 text-slate-300"
                }`}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Who's walking */}
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-teal-400">Who's walking?</label>
          <div className="mt-1 grid grid-cols-2 gap-2">
            {[["employee", "EMPLOYEE / TECH", "Live camera + optional photo picker"], ["tenant", "TENANT", "Magic link · live camera only, time-stamped"]].map(([k, l, sub]) => (
              <button key={k} onClick={() => setWalker(k)} data-testid={`walker-${k}`}
                className={`h-14 rounded-xl border px-3 text-left ${walker === k ? "bg-teal-500 border-teal-400 text-slate-900" : "bg-white/5 border-white/10 text-slate-300"}`}>
                <div className="font-black uppercase text-[11px] tracking-widest">{l}</div>
                <div className={`text-[10px] leading-tight ${walker === k ? "text-slate-800" : "text-slate-400"}`}>{sub}</div>
              </button>
            ))}
          </div>
          {walker === "employee" && (
            <div className="mt-2 flex items-center gap-2" data-testid="allow-library-row">
              <span className="text-xs font-bold text-slate-200 flex-1">Allow "Choose from library" photo picker?</span>
              {[["yes", true], ["no", false]].map(([l, v]) => (
                <button key={l} onClick={() => setAllowLibrary(v)} data-testid={`allow-library-${l}`}
                  className={`h-9 px-4 rounded-lg font-black text-[11px] uppercase ${allowLibrary === v ? "bg-teal-500 text-slate-900" : "bg-white/10 text-slate-300"}`}>{l}</button>
              ))}
            </div>
          )}
          {walker === "tenant" && <div className="mt-2 text-xs text-slate-400">A tenant link is created and copied when you hit Create. The tenant can't use old camera-roll photos.</div>}
        </div>

        {/* Address typeahead */}
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-teal-400">Property address</label>
          <input list="fr-inspection-addresses" value={address} onChange={e => setAddress(e.target.value)}
            data-testid="new-address-input"
            placeholder="Type — e.g. 240 Hermitage"
            className="mt-1 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-slate-500"/>
          <datalist id="fr-inspection-addresses">
            {addresses.slice(0, 300).map(a => <option key={a.address} value={a.address}/>)}
          </datalist>
        </div>

        {/* Due date */}
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-teal-400">Due date</label>
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
            data-testid="new-due-date"
            className="mt-1 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-sm font-mono text-white [color-scheme:dark]"/>
        </div>

        {/* Unit config — matches InspectRadar pattern */}
        <div className="rounded-2xl border border-teal-400/30 bg-teal-500/10 p-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-teal-300 flex items-center gap-1">
            📐 Unit layout — build the checklist to fit
            {autofillSource && (
              <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-emerald-300 inline-flex items-center gap-1"
                data-testid="autofill-hint">
                <Sparkle weight="fill" size={11}/> Auto-filled from {autofillSource === "prior_inspection" ? "prior inspection" : "AppFolio"}
              </span>
            )}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {[
              { label: "BEDROOMS", val: beds, set: setBeds },
              { label: "FULL BATHS", val: fullBaths, set: setFullBaths },
              { label: "HALF BATHS", val: halfBaths, set: setHalfBaths },
            ].map(c => (
              <div key={c.label}>
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">{c.label}</div>
                <input type="number" min={0} max={12} value={c.val}
                  onChange={e => c.set(parseInt(e.target.value || 0, 10))}
                  className="mt-1 w-full h-10 px-2 rounded-lg bg-[#0b1c2d] border border-white/15 text-sm font-mono text-white"/>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[9px] font-black uppercase tracking-widest text-slate-400">Optional rooms</div>
          <div className="mt-1 grid grid-cols-3 gap-x-2 gap-y-1">
            {OPTIONAL_ROOMS.map(r => (
              <label key={r} className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-slate-200 accent-teal-500">
                <input type="checkbox" checked={!!rooms[r]}
                  onChange={e => setRooms(x => ({ ...x, [r]: e.target.checked }))}/>
                {r}
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button onClick={onClose}
            className="flex-1 h-12 rounded-xl bg-white/10 hover:bg-white/15 font-black uppercase text-xs tracking-widest">Cancel</button>
          <button onClick={submit} disabled={creating || !address.trim()}
            data-testid="new-create-btn"
            className="flex-1 h-12 rounded-xl bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-slate-900 font-black uppercase text-xs tracking-widest">
            {creating ? "Creating…" : "Create Inspection"}
          </button>
        </div>
      </div>
    </div>
  );
}
